import { useState } from 'react'
import { View, Linking } from 'react-native'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useAuthRhythm } from '../../lib/hooks/use-auth-rhythm'
import { useBreakpoint } from '../../lib/hooks/use-breakpoint'
import { AuthGradientButton } from '../../components/auth/AuthGradientButton'
import { AuthScaffold } from '../../components/auth/AuthScaffold'
import { impact, notification, ImpactStyle, NotificationType } from '@/lib/utils/haptics'
import Animated, { FadeInDown, FadeInUp, useReducedMotion } from 'react-native-reanimated'
import { Mail } from '@/components/ui/icons'
import { AuthErrorBanner } from '../../components/auth/AuthErrorBanner'
import { AuthFooterLink } from '../../components/auth/AuthFooterLink'
import { AuthHeader } from '../../components/auth/AuthHeader'
import { AuthSuccessPanel } from '../../components/auth/AuthSuccessPanel'
import { TextInput } from '../../components/ui/TextInput'
import { BackButton } from '../../components/ui/BackButton'
import { useAuth } from '../../lib/hooks/use-auth'
import { forgotPasswordSchema } from '../../lib/validations/auth.schema'
import { COLORS, SPACING } from '../../lib/brand'
import { useSemanticColors, withAlpha, DURATION, STAGGER } from '../../lib/theme/tokens'
import { useTheme } from '../../lib/hooks/use-theme'
import { toast } from '../../lib/toast'
import { captureError, errorMessage } from '../../lib/utils/observability'

export default function ForgotPasswordScreen() {
  const { t } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { isDesktop } = useBreakpoint()
  const { resetPassword, resetPasswordPending } = useAuth()
  const reducedMotion = useReducedMotion()

  const iconColor = colors.textMuted

  const [email, setEmail] = useState('')
  const [fieldError, setFieldError] = useState('')
  const [submitError, setSubmitError] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const sp = useAuthRhythm()

  /**
   * One field and one button used to ration themselves into the top 55% of a
   * phone and leave the rest of the display empty. The stacked layout hands
   * this body the whole viewport (`flexGrow` on the scroller's content), so
   * grow into it and centre the block under the pinned back button. The split
   * layout already centres its own column — leave that one alone.
   */
  const bodyStyle = isDesktop ? undefined : { flexGrow: 1, justifyContent: 'center' as const }

  const handleOpenMail = () => {
    // `mailto:` has no handler on desktop web and on devices with no mail
    // client configured. Unawaited, the rejection became an unhandled promise
    // and the tap did nothing at all — restate what the user should do instead.
    void Linking.openURL('mailto:').catch((err: unknown) => {
      toast.info(t('auth.forgot_password_sent'))
      captureError(err, { screen: 'forgot-password', action: 'open-mail' })
    })
  }

  const handleSubmit = async () => {
    impact(ImpactStyle.Light)

    const result = forgotPasswordSchema.safeParse({ email })
    if (!result.success) {
      setFieldError(t(result.error.issues[0]?.message ?? 'auth.errors.signup_failed'))
      setSubmitError('')
      void notification(NotificationType.Error)
      return
    }
    setFieldError('')
    setSubmitError('')

    try {
      await resetPassword(result.data)
    } catch (err) {
      captureError(err, { screen: 'forgot-password', action: 'reset-password' })
      setSubmitError(errorMessage(err, t('auth.errors.email_verification_failed')))
      void notification(NotificationType.Error)
      return
    }

    void notification(NotificationType.Success)
    setSubmitted(true)
  }

  // ============================
  //  SUCCESS STATE
  // ============================
  if (submitted) {
    return (
      <AuthScaffold
        brandHeadline={t('welcome.headline')}
        brandSubtitle={t('welcome.subheadline')}
        background={colors.background}
      >
        <AuthSuccessPanel
          icon={<Mail size={40} color={COLORS.coral.DEFAULT} />}
          iconBackground={withAlpha(COLORS.coral.DEFAULT, isDark ? 0.15 : 0.1)}
          title={t('auth.forgot_password_title')}
          description={t('auth.forgot_password_sent')}
          testID="forgot.success-message"
        >
          {/* Actions */}
          <View style={{ width: '100%', gap: SPACING.sm + SPACING.xs }}>
            {/* Check Email — canonical bordered secondary. This used to be a
                hand-rolled Pressable with the same radius, padding and height
                as the shared secondary variant but its own grey border and
                body-weight label: two outline buttons in the same app drawn by
                two different hands. */}
            <AuthGradientButton
              variant="secondary"
              title={t('auth.verify_email_check')}
              onPress={handleOpenMail}
              testID="forgot.check-email-button"
              entering={
                reducedMotion ? undefined : FadeInUp.duration(DURATION.normal).delay(STAGGER * 8)
              }
            />

            {/* Sign In — gradient CTA. The button fires its own press-in
                haptic; the explicit `impact` here double-tapped the taptic
                engine on the one screen that had it. */}
            <AuthGradientButton
              title={t('auth.sign_in_button')}
              onPress={() => router.replace('/(auth)/login')}
              testID="forgot.sign-in-button"
              entering={
                reducedMotion ? undefined : FadeInUp.duration(DURATION.normal).delay(STAGGER * 10)
              }
            />
          </View>
        </AuthSuccessPanel>
      </AuthScaffold>
    )
  }

  // ============================
  //  FORM CONTENT
  // ============================
  const formContent = (
    <>
      {/* ─── Navigation ─── */}
      <BackButton testID="forgot.back-button" style={{ marginBottom: SPACING.sm }} />

      {/* ─── Body ─── */}
      <View style={bodyStyle}>
        {/* ─── Headline + subtitle ─── */}
        <AuthHeader
          title={t('forgot_password.headline')}
          subtitle={t('forgot_password.subtitle')}
          subtitleTone="primary"
        />

        {/* ─── Email field ─── */}
        <Animated.View
          collapsable={false}
          entering={
            reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER * 4)
          }
        >
          <TextInput
            label={t('auth.email')}
            placeholder={t('auth.email_placeholder')}
            value={email}
            onChangeText={(v) => {
              setEmail(v)
              setFieldError('')
              setSubmitError('')
            }}
            keyboardType="email-address"
            autoCapitalize="none"
            autoComplete="email"
            textContentType="emailAddress"
            returnKeyType="go"
            onSubmitEditing={handleSubmit}
            error={fieldError}
            leftIcon={<Mail size={18} color={iconColor} />}
            testID="forgot.email-input"
          />
        </Animated.View>

        {/* ─── Action zone ─── */}
        <View style={{ marginTop: sp.formToAction }}>
          {/* Error banner */}
          <AuthErrorBanner error={submitError || null} />

          {/* CTA */}
          <AuthGradientButton
            title={t('auth.send_reset_link')}
            onPress={handleSubmit}
            loading={resetPasswordPending}
            testID="forgot.submit-button"
            entering={
              reducedMotion ? undefined : FadeInUp.duration(DURATION.normal).delay(STAGGER * 4)
            }
          />
        </View>

        {/* ─── Footer — back to login ─── */}
        <AuthFooterLink
          prompt={t('auth.already_have_account')}
          linkLabel={t('auth.login')}
          onPress={() => router.replace('/(auth)/login')}
          accessibilityLabel={`${t('auth.already_have_account')} ${t('auth.login')}`}
          testID="forgot.login-link"
        />
      </View>
    </>
  )

  return (
    <AuthScaffold
      brandHeadline={t('welcome.headline')}
      brandSubtitle={t('welcome.subheadline')}
      background={colors.background}
    >
      {formContent}
    </AuthScaffold>
  )
}
